/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {JobProgressUpdate} from '@ion/services/src/websocket/types';
import {toNumericIds} from './JobSearchEngineUtils';
import {JobSubscriptionConsumer, register, sync} from './JobSubscriptions';

export interface JobSubscriptionCallbacks {
  onDelta?: (delta: JobProgressUpdate, jobId: number) => void,
  onResync?: () => void
}

export interface JobSubscriptionResult {
  observe: (ids: (number | string)[]) => void
}

/**
 * Declares the job ids a surface wants observed for `jobProgress` changes, and
 * receives each change routed to those ids. Everything is imperative, matching
 * the non-reactive registry behind it: nothing here re-renders the caller.
 *
 * `observe(ids)` replaces this consumer's id set - it does not add to it - and
 * re-runs the union diff, so declaring the ids of a newly loaded page is also
 * the entire unsubscribe path for the page being left.
 *
 * ```typescript
 *
 *   import {useJobSubscriptions} from './useJobSubscriptions';
 *
 *   const {observe} = useJobSubscriptions({onDelta, onResync});
 *
 *   // ... once a block of jobs has loaded ...
 *   observe(response.items.map(item => item.jobId));
 *
 * ```
 *
 * @param   onDelta Invoked with each change received for a declared id, and
 *          the numeric job id it arrived for.
 * @param   onResync Invoked after the socket reconnects, when deltas emitted
 *          during the outage have been lost and the surface must refetch.
 * @returns An `observe` function which declares this consumer's ids.
 */
export const useJobSubscriptions = ({
  onDelta,
  onResync
}: JobSubscriptionCallbacks): JobSubscriptionResult => {
  const consumer = useRef<JobSubscriptionConsumer>({ids: []}).current;

  useEffect(() => {
    consumer.onDelta  = onDelta;
    consumer.onResync = onResync;
  });

  useEffect(() => register(consumer), []);

  const observe = useCallback((ids: (number | string)[]): void => {
    consumer.ids = toNumericIds(ids);
    sync();
  }, []);

  return {observe};
};

export default useJobSubscriptions;
